import cn from "classnames";
import { Text } from "../../../../../packages/ui-kit";
import { useToggleState } from "../../../../../packages/hooks";
import { helpers } from "../../../../../packages/helpers";
import { WarningIcon } from "../../../../../packages/icons";
import { FileIconComponent, RemoveIconComponent } from "../../file-icon";
import type { AttachedFile } from "../../../types";
import styles from "./file-list.module.scss";

const { getFileSize } = helpers.math;
const { getBreakText } = helpers.text;

type FileProps = Omit<AttachedFile & { onRemoveClick: () => void }, "id">;

export const File = ({ error, file, onRemoveClick }: FileProps) => {
  const {
    toggle: shouldShownError,
    toggleOn: showError,
    toggleOff: hideError,
  } = useToggleState();

  const fileIconStyles = cn(styles["file-icon"], {
    [styles["file-error"]]: error,
  });

  const fileNameStyles = cn(styles["file-name"], {
    [styles["file-error"]]: error,
  });

  const fileSizeStyles = cn(styles["file-size"], {
    [styles["file-error"]]: error,
  });

  const fileWarningIconStyles = cn(
    styles["file-warning-icon"],
    styles["file-error"]
  );

  const fileIcon = (
    <div className={fileIconStyles}>
      <FileIconComponent filename={file.name} />
    </div>
  );

  const errorIcon = (
    <div
      className={fileWarningIconStyles}
      onMouseEnter={showError}
      onMouseLeave={hideError}
    >
      <WarningIcon size="medium" />
    </div>
  );

  const removeIcon = <RemoveIconComponent onClick={onRemoveClick} />;

  const errorText = (
    <Text size="medium" className={fileNameStyles}>
      {getBreakText(error)}
    </Text>
  );

  const infoText = (
    <>
      <Text
        dataTestId={`file-name:${file.name}`}
        size="medium"
        className={fileNameStyles}
      >
        {file.name}
      </Text>

      <Text
        dataTestId={`file-size:${file.size}`}
        size="medium"
        className={fileSizeStyles}
      >
        {getFileSize(file.size)}
      </Text>
    </>
  );

  const info = (
    <div className={styles["file-info"]}>
      {error && shouldShownError ? errorText : infoText}
    </div>
  );

  return (
    <div data-test-id="attached-file" className={styles["attached-file"]}>
      {fileIcon}
      {info}
      {error && errorIcon}
      {removeIcon}
    </div>
  );
};
